/**
 * URL sanitisation for snapshot fields.
 *
 * Strips credential-bearing query parameters by key name, and replaces UUID
 * path segments with `[id]`. Numeric IDs and other opaque identifiers are left
 * intact — the customer already sent them to their analytics provider, and they
 * carry useful page-context for an LLM (product IDs, order numbers, pagination).
 *
 * UUIDs are the exception: they are opaque by design, add no LLM context, and
 * are a near-universal pattern for user/session/resource identifiers.
 */

/**
 * Query-parameter names whose values are always redacted. Lowercased; the
 * matcher is case-insensitive.
 */
const SENSITIVE_QUERY_KEYS = new Set<string>([
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'auth_token',
  'authtoken',
  'jwt',
  'session',
  'sessionid',
  'session_id',
  'sid',
  'sso',
  'code',
  'state',
  'nonce',
  'api_key',
  'apikey',
  'api-key',
  'key',
  'secret',
  'password',
  'passwd',
  'pwd',
  'auth',
  'authorization',
  'bearer',
  'cookie',
  'reset_password',
  'reset_token',
  'verify',
  'verification',
  'oauth_token',
  'oauth_verifier',
]);

/** Standard UUID v1–v5 (8-4-4-4-12 hex groups). */
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Sanitise a URL for safe inclusion in an LLM snapshot.
 *
 * Returns the sanitised URL string, or the input truncated to `maxLength`
 * if parsing fails (some "URLs" we see are fragments or relative paths).
 */
export function redactUrl(input: string, maxLength = 512): string {
  if (!input) return input;
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    try {
      // Relative path — parse with stub base, emit path + query only.
      url = new URL(input, 'http://placeholder.invalid/');
      return redactPathAndQuery(url, maxLength);
    } catch {
      return input.replace(UUID_RE, '[id]').slice(0, maxLength);
    }
  }
  return redactFullUrl(url, maxLength);
}

function redactFullUrl(url: URL, maxLength: number): string {
  redactQueryParams(url);
  url.pathname = url.pathname.replace(UUID_RE, '[id]');
  return url.toString().slice(0, maxLength);
}

function redactPathAndQuery(url: URL, maxLength: number): string {
  redactQueryParams(url);
  const path = url.pathname.replace(UUID_RE, '[id]');
  return `${path}${url.search}`.slice(0, maxLength);
}

function redactQueryParams(url: URL): void {
  const keys = Array.from(new Set(Array.from(url.searchParams.keys())));
  for (const key of keys) {
    if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
      url.searchParams.set(key, '[REDACTED]');
    }
  }
}
